import classcat from "classcat";
import React from "react";

import { IconTag } from "@touchtech/web-baselayer-ui";

import { ADDITIONAL_OFFSET } from "@is/constants";
import PendingSpinner from "@is/features/ContentView/PendingSpinner";
import { Section } from "@is/types";

import ContentItemComponent from "../ContentItemComponent";
import css from "./ContentSection.module.css";
import useManageContentSection from "./hooks/useManageContentSection";

type Props = {
  contentItemsRefs?: React.RefObject<Record<string, HTMLDivElement>>;
  contentItemsAnchorRefs?: React.RefObject<Record<string, HTMLDivElement>>;
  searchDrawerHeight?: number;
  section: Section;
  setShowGoToBottomButton?: (value: boolean) => void;
};

const ContentSection = ({
  contentItemsRefs,
  contentItemsAnchorRefs,
  searchDrawerHeight,
  section,
  setShowGoToBottomButton,
}: Props) => {
  const {
    t,
    searchTerms,
    sectionHeaderSizesAndLength,
    sectionHeaderWithoutSizesAndLength,
    isOnlySection,
    noMatchingItems,
    isLastSection,
    contentItems,
    sectionId,
    showMorePlaceholder,
    handleShowMore,
    handleSectionHeaderClick,
    showSearchAndFooterDrawers,
    offset,
    isPending,
  } = useManageContentSection(section);

  return (
    <div className={css.sectionContainer} style={{ marginTop: offset }}>
      {!!searchTerms?.length && (
        <div
          className={css.sectionHeaderContainer}
          onClick={handleSectionHeaderClick}
        >
          {!!sectionHeaderWithoutSizesAndLength && (
            <div
              className={classcat([
                css.sectionHeader,
                "text-label-lg",
                {
                  [css.noMatchingOnlySection]: isOnlySection && noMatchingItems,
                },
              ])}
            >
              {sectionHeaderWithoutSizesAndLength}
            </div>
          )}
          {sectionHeaderSizesAndLength && (
            <div
              className={classcat([
                css.sectionHeader,
                css.sizeAndLengthHeader,
                "text-label-lg",
                {
                  [css.noMatchingOnlySection]: isOnlySection && noMatchingItems,
                  [css.noLeftBorder]: !!sectionHeaderWithoutSizesAndLength,
                },
              ])}
            >
              <IconTag />
              {sectionHeaderSizesAndLength}
            </div>
          )}
        </div>
      )}
      {noMatchingItems ? (
        <div className={css.noMatchingContainer}>
          {(isLastSection || isOnlySection) && (
            <>
              <h1
                className={classcat(["text-headline-5xl", css.noMatchingTitle])}
              >
                {t("No Matching Items Found")}
              </h1>
              <span
                className={classcat([
                  "text-label-xl",
                  css.noMatchingSubtitle,
                  { [css.lastSectionPadding]: isLastSection },
                ])}
              >
                {t("Adjust your search terms for better results.")}
              </span>
            </>
          )}
        </div>
      ) : (
        contentItems.map((item, index) => (
          <ContentItemComponent
            contentItemsRefs={contentItemsRefs}
            key={item.id}
            item={item}
            itemIndex={index}
            searchDrawerHeight={
              showSearchAndFooterDrawers
                ? searchDrawerHeight + ADDITIONAL_OFFSET
                : 0
            }
            sectionId={sectionId}
            contentItemsAnchorRefs={contentItemsAnchorRefs}
            setShowGoToBottomButton={setShowGoToBottomButton}
            handleShowMore={handleShowMore}
          />
        ))
      )}
      <PendingSpinner show={isPending} height={"80vh"} />
      {showMorePlaceholder}
    </div>
  );
};

export default ContentSection;
